import { useId } from 'react';
import type { Locale } from '@xfile/shared';

/**
 * Petit drapeau national (SVG inline) accompagnant le nom de langue dans le
 * sélecteur de connexion. Décoratif (`aria-hidden`) : le libellé textuel porte le
 * sens. Ce ne sont PAS des icônes d'UI (pas de token sémantique) mais l'ARTWORK
 * fixe d'un drapeau — d'où les couleurs littérales, intrinsèques au drapeau. SVG
 * inline (jamais d'emoji ni d'icon-font, conforme à la règle projet).
 */
export function Flag({
  locale,
  className,
}: {
  locale: Locale;
  className?: string;
}): React.JSX.Element {
  const clip = useId();
  const chip = `inline-block overflow-hidden rounded-[2px] ring-1 ring-black/10 ${className ?? 'h-4 w-6'}`;

  if (locale === 'fr') {
    return (
      <span className={chip} aria-hidden="true">
        <svg viewBox="0 0 3 2" preserveAspectRatio="none" className="h-full w-full">
          <rect width="1" height="2" x="0" fill="#0055A4" />
          <rect width="1" height="2" x="1" fill="#FFFFFF" />
          <rect width="1" height="2" x="2" fill="#EF4135" />
        </svg>
      </span>
    );
  }

  if (locale === 'de') {
    return (
      <span className={chip} aria-hidden="true">
        <svg viewBox="0 0 5 3" preserveAspectRatio="none" className="h-full w-full">
          <rect width="5" height="1" y="0" fill="#000000" />
          <rect width="5" height="1" y="1" fill="#DD0000" />
          <rect width="5" height="1" y="2" fill="#FFCE00" />
        </svg>
      </span>
    );
  }

  // Anglais → Union Jack (drapeau du Royaume-Uni). Les `clipPath` sont namespacés
  // par `useId` → aucune collision d'id quand plusieurs drapeaux coexistent (menu).
  return (
    <span className={chip} aria-hidden="true">
      <svg viewBox="0 0 60 30" preserveAspectRatio="none" className="h-full w-full">
        <clipPath id={`${clip}-s`}>
          <path d="M0,0 v30 h60 v-30 z" />
        </clipPath>
        <clipPath id={`${clip}-t`}>
          <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <g clipPath={`url(#${clip}-s)`}>
          <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
          <path
            d="M0,0 L60,30 M60,0 L0,30"
            clipPath={`url(#${clip}-t)`}
            stroke="#C8102E"
            strokeWidth="4"
          />
          <path d="M30,0 v30 M0,15 h60" stroke="#FFFFFF" strokeWidth="10" />
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </g>
      </svg>
    </span>
  );
}
